import {
  ArchiveIcon,
  BackIcon,
  BottomSheet,
  Button,
  ForwardIcon,
  IconButton,
  NextIcon,
  PreviousIcon,
  RestoreIcon,
  Stack,
  StepTracker,
  Tag,
  type TagColor,
  Typography,
  ValidateIcon,
  ViewIcon,
  WorkspaceDialog,
} from '@all-manager/design-system-v2';
import type { InboxEmail, InboxEmailStatus } from '../../types/inbox.types';
import EmailContentView from '../EmailContentView/EmailContentView';
import GuidedProcessingWorkflow from '../GuidedProcessingWorkflow/GuidedProcessingWorkflow';
import { useEmailDetailModal } from './useEmailDetailModal';

type Props = {
  open: boolean;
  email: InboxEmail | null;
  emails: InboxEmail[];
  /** Emails in the current tab and search, loaded or not. */
  totalEmails: number;
  hasMoreEmails: boolean;
  onLoadMoreEmails: () => Promise<InboxEmail[]>;
  spaceId: string;
  onClose: () => void;
  onSelectEmail: (email: InboxEmail) => void;
};

const STATUS_TAGS: Record<InboxEmailStatus, { color: TagColor; label: string }> = {
  pending: { color: 'pending', label: 'À traiter' },
  processed: { color: 'done', label: 'Traité' },
  ignored: { color: 'default', label: 'Ignoré' },
};

export default function EmailDetailModal(props: Props) {
  const hook = useEmailDetailModal(props);
  if (!hook.email) return null;

  const email: InboxEmail = hook.email;

  const statusTag = STATUS_TAGS[email.status] ?? STATUS_TAGS.ignored;
  const isPending = email.status === 'pending';
  const isWorkflowScreen = hook.isAdmin && hook.isMobile && hook.mobileView === 'workflow' && hook.currentStep !== null;

  const emailContent = (
    <EmailContentView
      email={email}
      activeViewMode={hook.activeViewMode}
      hasHtml={hook.hasHtml}
      cleanedBodyText={hook.cleanedBodyText}
      relevantAttachments={hook.relevantAttachments}
      signatureAttachments={hook.signatureAttachments}
      showSignatureAttachments={hook.showSignatureAttachments}
      onToggleViewMode={hook.handleToggleViewMode}
      onToggleSignatureAttachments={hook.handleToggleSignatureAttachments}
      onDownloadAttachment={hook.handleDownloadAttachment}
    />
  );

  const status = <Tag color={statusTag.color} label={statusTag.label} variantIcon="state" />;

  function renderDesktopActions() {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {hook.totalEmails > 1 && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2">
              {hook.currentIndex + 1} sur {hook.totalEmails}
            </Typography>
            <IconButton tooltip="E-mail précédent" size="small" onClick={hook.handlePrev} disabled={!hook.hasPrev}>
              <PreviousIcon fontSize="small" />
            </IconButton>
            <IconButton tooltip="E-mail suivant" size="small" onClick={hook.handleNext} disabled={!hook.hasNext}>
              <NextIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
        {!hook.isAdmin ? null : isPending ? (
          <>
            <Button
              variant="text"
              size="small"
              color="default"
              startIcon={<ArchiveIcon />}
              disabled={hook.isUpdatingStatus}
              onClick={hook.handleMarkAsIgnored}
            >
              Ignorer
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<ValidateIcon />}
              disabled={hook.isUpdatingStatus}
              onClick={hook.handleMarkAsProcessed}
            >
              Marquer comme traité
            </Button>
          </>
        ) : (
          <Button
            variant="text"
            size="small"
            color="default"
            startIcon={<RestoreIcon />}
            disabled={hook.isUpdatingStatus}
            onClick={hook.handleRestore}
          >
            Rétablir
          </Button>
        )}
      </Stack>
    );
  }

  function renderDesktop() {
    // Read-only viewers only consult the email: no processing workflow
    if (!hook.isAdmin) {
      return (
        <WorkspaceDialog
          open={props.open}
          onClose={props.onClose}
          closeLabel="Fermer"
          backLabel="Retour"
          onBack={props.onClose}
          title={email.subject}
          status={status}
          actions={renderDesktopActions()}
        >
          {emailContent}
        </WorkspaceDialog>
      );
    }

    return (
      <WorkspaceDialog
        open={props.open}
        onClose={props.onClose}
        closeLabel="Fermer"
        backLabel="Retour"
        onBack={props.onClose}
        title={email.subject}
        status={status}
        actions={renderDesktopActions()}
        aside={emailContent}
      >
        <GuidedProcessingWorkflow email={email} spaceId={props.spaceId} onSuccess={props.onClose} />
      </WorkspaceDialog>
    );
  }

  function renderMobileEmailScreen() {
    return (
      <WorkspaceDialog
        open={props.open}
        onClose={props.onClose}
        closeLabel="Fermer"
        title={email.subject}
        status={status}
        footer={hook.isAdmin ? (
          <Stack spacing={1}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              endIcon={<ForwardIcon />}
              onClick={hook.handleGoToWorkflow}
            >
              Passer aux étapes ({hook.workflowSteps.length})
            </Button>
            {isPending && (
              <>
                <Button
                  variant="text"
                  size="large"
                  color="default"
                  fullWidth
                  startIcon={<ValidateIcon />}
                  disabled={hook.isUpdatingStatus}
                  onClick={hook.handleMarkAsProcessed}
                >
                  Marquer comme traité sans étape
                </Button>
                <Button
                  variant="text"
                  size="large"
                  color="default"
                  fullWidth
                  startIcon={<ArchiveIcon />}
                  disabled={hook.isUpdatingStatus}
                  onClick={hook.handleMarkAsIgnored}
                >
                  Ignorer cet e-mail
                </Button>
              </>
            )}
          </Stack>
        ) : undefined}
      >
        {emailContent}
      </WorkspaceDialog>
    );
  }

  function renderMobileWorkflowScreen() {
    const currentStep = hook.currentStep;
    if (!currentStep) return null;

    return (
      <WorkspaceDialog
        open={props.open}
        onClose={props.onClose}
        closeLabel="Fermer"
        overline={`Étape ${hook.activeStepIndex + 1}/${hook.workflowSteps.length}`}
        title={currentStep.shortTitle}
        actions={(
          <Button variant="text" size="small" color="default" startIcon={<ViewIcon />} onClick={hook.handleOpenEmailDrawer}>
            Voir l'e-mail
          </Button>
        )}
        footer={(
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="text"
              size="large"
              color="default"
              startIcon={<BackIcon />}
              onClick={hook.handleBackFromWorkflow}
            >
              {hook.activeStepIndex === 0 ? 'E-mail' : 'Précédent'}
            </Button>
            {hook.isLastStep ? (
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<ValidateIcon />}
                disabled={hook.isUpdatingStatus}
                onClick={hook.handleMarkAsProcessed}
              >
                Terminer et traiter
              </Button>
            ) : (
              <Button variant="contained" size="large" fullWidth endIcon={<ForwardIcon />} onClick={hook.handleNextStep}>
                Suivant
              </Button>
            )}
          </Stack>
        )}
      >
        <Stack spacing={2}>
          <StepTracker
            aria-label="Étapes de traitement"
            steps={hook.stepTrackerSteps}
            activeIndex={hook.activeStepIndex}
            onSelect={hook.handleSelectStep}
          />
          <Stack spacing={0.5}>
            <Typography variant="h6">{currentStep.title}</Typography>
            <Typography variant="body2">{currentStep.description}</Typography>
          </Stack>
          <GuidedProcessingWorkflow
            email={email}
            spaceId={props.spaceId}
            activeStepId={currentStep.id}
            hideHeader
            onSuccess={props.onClose}
          />
        </Stack>
      </WorkspaceDialog>
    );
  }

  function renderDialog() {
    if (!hook.isMobile) return renderDesktop();
    if (isWorkflowScreen) return renderMobileWorkflowScreen();
    return renderMobileEmailScreen();
  }

  return (
    <>
      {renderDialog()}
      <BottomSheet
        open={hook.isEmailDrawerOpen}
        onClose={hook.handleCloseEmailDrawer}
        title="Aperçu de l'e-mail"
        subtitle={email.subject}
        closeLabel="Fermer l'aperçu"
      >
        {emailContent}
      </BottomSheet>
    </>
  );
}
